import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  camOn,
  defaultFeed,
  defaultOwnFeed,
  geoStatusLabel,
  micOn,
  onAirLight,
  type Coords,
  type OwnFeed,
} from '@rabbitfuel/shared';
import { useRealtime } from '../lib/realtime.tsx';
import { useStageSurface, useWakeLock } from '../lib/ui.ts';
import { cameraTracks, useAudioMonitors, useFeedGate, usePublisher, useVideoStream } from '../lib/useMedia.ts';
import { useTalkReceiver } from '../lib/talk.ts';
import { ConnPill, StageLegal } from '../components/Chrome.tsx';
import { AppearanceControls } from '../components/AppearanceControls.tsx';
import { LevelMeter } from '../components/LevelMeter.tsx';
import { OnAirLamp } from '../components/OnAirLamp.tsx';
import { ClipRecorder } from '../components/ClipRecorder.tsx';
import { bestLocation } from '../lib/locate.ts';
import { mediaErrorText, openCamera } from '../lib/webrtc.ts';
import { roleLabel } from '../lib/session.ts';

const labelPresets = ['Ich-Perspektive', 'Startlinie', 'Km 5', 'Verpflegung', 'Zielkurve', 'Fankurve'];


/**
 * So lange darf das Mikrofon nach einer Unterbrechung stumm bleiben, bevor
 * es neu geoeffnet wird -- iOS gibt es nach dem Sperrbildschirm oft von
 * selbst zurueck, und ein Neuoeffnen kostet einen Ruckler im Bild.
 */
const MIC_GRACE_MS = 2000;
/** Danach entscheidet die Person selbst -- per Knopf, also mit einem Tipp. */
const MIC_AUTO_RESTARTS = 2;

type MicState = 'live' | 'muted' | 'ended' | 'none';

/**
 * Was diese Person selbst ausgeschaltet hat.
 *
 * Gilt sofort an den eigenen Spuren, ohne erst auf den Server zu warten --
 * im Zweifel ist das genau der Moment, in dem es schnell gehen muss. Und es
 * ueberlebt einen Reload: wer sein Bild abgeblendet hat, soll nach F5 nicht
 * ungefragt wieder senden.
 */
function useOwnFeed(slug: string) {
  const key = `rabbitfuel:own:${slug}`;
  const [own, setOwn] = useState<OwnFeed>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? { ...defaultOwnFeed(), ...(JSON.parse(raw) as Partial<OwnFeed>) } : defaultOwnFeed();
    } catch {
      return defaultOwnFeed();
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(own));
    } catch {
      /* Privater Modus -- dann gilt es eben nur bis zum Reload. */
    }
  }, [key, own]);
  return [own, setOwn] as const;
}

/** So lange wartet die Anfrage auf eine Antwort, bevor die Seite sagt, dass etwas hakt. */
const REQUEST_ANSWER_MS = 8000;

/** So hoch geht das Livebild dieser Seite hoechstens raus -- der Stream sendet 720p. */
const LIVE_MAX_HEIGHT = 720;

export function Broadcast() {
  const { slug = '' } = useParams();
  useStageSurface();
  const { state, send } = useRealtime();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facing, setFacing] = useState<'user' | 'environment'>('environment');
  const [label, setLabel] = useState(labelPresets[0]);
  const [camError, setCamError] = useState<string | null>(null);
  /** Zaehlt hoch, um Kamera und Mikrofon neu zu oeffnen. */
  const [reopen, setReopen] = useState(0);
  const videoRef = useVideoStream(stream);
  const liveSentFor = useRef<string | null>(null);
  // Ein Handy an der Strecke beruehrt niemand -- ohne das sperrt es sich und
  // die Kamera steht.
  useWakeLock(Boolean(stream));

  const [locating, setLocating] = useState(false);
  /** Aufnahmefenster fuer eine Botschaft -- geoeffnet vom Knopf neben Mikro und Kamera oder unten im Kasten. */
  const [clipOpen, setClipOpen] = useState(false);
  const clipsOff = state.myClips.policy === 'off';
  const [geoError, setGeoError] = useState<string | null>(null);
  const fence = state.event?.geofence ?? null;

  const mySource = useMemo(
    () => state.sources.find((s) => s.connectionId === state.self?.connectionId) ?? null,
    [state.sources, state.self],
  );

  // Die Quelle adressiert den Publisher: der Server sagt beim Abonnieren, wer
  // gemeint ist, und nur diese eine Kamera sendet dieses Handy. Raus geht
  // hoechstens 720p -- auch waehrend die Kamera fuer eine Botschaft 1080p liefert.
  const subscriberCount = usePublisher(stream, mySource?.id, LIVE_MAX_HEIGHT);

  /*
   * Fuer eine Videobotschaft liefert die Kamera kurz 1080p, danach wieder
   * 720p. Nur fuer die Dauer der Aufnahme: ueber Stunden in 1080p wuerde das
   * Handy waermer und leerer, ohne dass der Livestream etwas davon haette.
   */
  const setClipHd = useCallback(
    async (on: boolean) => {
      const track = stream?.getVideoTracks()[0];
      if (!track || track.readyState !== 'live') return;
      await track
        .applyConstraints({
          width: { ideal: on ? 1920 : 1280 },
          height: { ideal: on ? 1080 : 720 },
          frameRate: { ideal: 30 },
        })
        .catch(() => undefined);
    },
    [stream],
  );

  /*
   * Die Regie kann Mikrofon und Bild dieser Kamera abschalten -- abgeschaltet
   * wird es hier, an den eigenen Spuren. Das ist die einzige Stelle, an der
   * das ehrlich geht: was dieses Geraet nicht sendet, kann auch niemand
   * weiterreichen.
   *
   * Dazu kommen die eigenen Schalter dieser Person. Sie stehen schon vor der
   * Anfrage zur Verfuegung -- wer stumm anfragen will, kann das.
   */
  const [own, setOwn] = useOwnFeed(slug);
  const feed = useMemo(() => ({ ...(mySource?.feed ?? defaultFeed()), own }), [mySource?.feed, own]);
  const tracks = useMemo(() => cameraTracks(stream), [stream]);
  useFeedGate(tracks, feed);

  /*
   * Der Server erfaehrt davon nur, damit Zuschauer das Standardbild statt
   * Schwarz sehen und die Regie weiss, wer abgeschaltet hat. Nachgezogen wird
   * bei jeder Abweichung -- auch nach einem Reload oder einer frisch
   * angelegten Anfrage, die beim Server noch alles an hat.
   */
  useEffect(() => {
    if (!mySource || mySource.status === 'rejected' || mySource.status === 'ended') return;
    if (mySource.feed.own.mic === own.mic && mySource.feed.own.cam === own.cam) return;
    send({ t: 'source:own', sourceId: mySource.id, patch: own });
  }, [mySource, own, send]);

  /*
   * Und deshalb muss es hier auch stehen. Ohne diesen Hinweis sucht jemand
   * den Fehler bei sich, waehrend in Wahrheit die Regie abgeblendet hat.
   */
  const feedNote = !mySource
    ? null
    : !mySource.feed.mic && !mySource.feed.cam
      ? 'Die Regie hat dein Bild und dein Mikrofon aus.'
      : !mySource.feed.mic
        ? 'Die Regie hat dein Mikrofon stumm geschaltet.'
        : !mySource.feed.cam
          ? 'Die Regie hat dein Bild abgeblendet — im Stream läuft das Standardbild.'
          : null;

  /** Standort fuer die Anfrage -- nur in einem Event mit Bereich, siehe `bestLocation`. */
  const currentLocation = async (): Promise<Coords | null> => {
    if (!fence || !navigator.geolocation) return null;
    setLocating(true);
    setGeoError(null);
    const { location, error } = await bestLocation();
    setLocating(false);
    setGeoError(error);
    return location;
  };

  /*
   * Rueckmeldung zur Anfrage. Frueher passierte nach dem Tippen sichtbar
   * nichts, bis die Regie-Liste sie zeigte -- und kam sie nie an (Verbindung
   * weg, Server ueberlastet), blieb es dabei. Jetzt steht „wird gesendet",
   * und nach ein paar Sekunden ohne Antwort ein Hinweis mit Ausweg.
   */
  const [requestedAt, setRequestedAt] = useState<number | null>(null);
  const [requestStuck, setRequestStuck] = useState(false);
  const requestStream = async () => {
    setRequestStuck(false);
    const location = await currentLocation();
    send({ t: 'stream:request', label, location });
    setRequestedAt(Date.now());
  };
  const answered = Boolean(mySource && mySource.status !== 'rejected') || Boolean(state.error);
  useEffect(() => {
    if (requestedAt === null) return;
    if (answered) {
      setRequestedAt(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setRequestedAt(null);
      setRequestStuck(true);
    }, REQUEST_ANSWER_MS);
    return () => window.clearTimeout(timer);
  }, [requestedAt, answered]);
  const requesting = requestedAt !== null;
  const isOnAir = mySource != null && state.activeSourceId === mySource.id;
  const inPicture = isOnAir || (mySource != null && state.overlaySourceId === mySource.id);

  /*
   * Gegensprechen: die Regie ruft dieses Handy ueber eine eigene Sprechleitung
   * an, sobald es im Bild ist oder sie per Taste spricht (talk.ts). Gespielt
   * wird ueber den Lautsprecher; dass die Regie nicht ueber das eigene
   * Mikrofon zurueck in den Stream laeuft, erledigt die Echo-Unterdrueckung
   * des Telefons -- dieselbe wie bei FaceTime.
   */
  const talk = useTalkReceiver();
  const talkAudio = useAudioMonitors(talk, { enabled: true });

  /* Kamera oeffnen / wechseln */
  useEffect(() => {
    let cancelled = false;
    let opened: MediaStream | null = null;
    openCamera(facing)
      // Ist das Mikrofon belegt (Anruf, Streaming-App, Bildschirmuebertragung)
      // oder verboten, scheitert alles zusammen. Dann wenigstens das Bild --
      // anfragen und senden geht damit trotzdem, und die Seite sagt es dazu.
      .catch((err: unknown) => openCamera(facing, {}, false).catch(() => Promise.reject(err)))
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        opened = s;
        setStream(s);
        setCamError(null);
      })
      .catch((e: unknown) => !cancelled && setCamError(mediaErrorText(e)));
    return () => {
      cancelled = true;
      opened?.getTracks().forEach((t) => t.stop());
    };
  }, [facing, reopen]);

  /*
   * Das Mikrofon im Blick behalten.
   *
   * iOS schaltet die Aufnahme bei jeder Unterbrechung stumm -- Anruf, Siri,
   * Sperrbildschirm, ein Wechsel in eine andere App. Das Bild kommt danach
   * zurueck, das Mikrofon nicht immer: die Spur bleibt `muted`, und im Stream
   * laeuft Bild ohne Ton, ohne dass es hier jemand merkt. Das ist die
   * naheliegende Erklaerung fuer „das iPhone sendet nur Bild".
   *
   * `muted` setzt allein der Browser. Das Abschalten durch die Regie laeuft
   * ueber `enabled` (useFeedGate) und loest hier deshalb nichts aus.
   */
  const micTrack = stream?.getAudioTracks()[0] ?? null;
  const [mic, setMic] = useState<MicState>('none');
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');
  const [micStuck, setMicStuck] = useState(false);
  const micRestarts = useRef(0);

  useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    if (!micTrack) {
      setMic('none');
      return;
    }
    const read = () => setMic(micTrack.readyState === 'ended' ? 'ended' : micTrack.muted ? 'muted' : 'live');
    read();
    micTrack.addEventListener('mute', read);
    micTrack.addEventListener('unmute', read);
    micTrack.addEventListener('ended', read);
    return () => {
      micTrack.removeEventListener('mute', read);
      micTrack.removeEventListener('unmute', read);
      micTrack.removeEventListener('ended', read);
    };
  }, [micTrack]);

  /*
   * Selbstheilung: bleibt das Mikrofon nach der Rueckkehr stumm, Kamera und
   * Mikrofon neu oeffnen. Der Publisher zieht laufende Verbindungen per
   * `replaceTrack` auf die neuen Spuren um -- wie beim Kamerawechsel. Nur im
   * Vordergrund: im Hintergrund gibt iOS ohnehin nichts heraus. Nach zwei
   * vergeblichen Versuchen hoert die Automatik auf und der Knopf erscheint.
   */
  useEffect(() => {
    if (mic === 'live') {
      micRestarts.current = 0;
      setMicStuck(false);
      return;
    }
    if (mic === 'none' || !visible) return;
    const timer = window.setTimeout(
      () => {
        if (micRestarts.current >= MIC_AUTO_RESTARTS) return setMicStuck(true);
        micRestarts.current += 1;
        setReopen((n) => n + 1);
      },
      mic === 'ended' ? 300 : MIC_GRACE_MS,
    );
    return () => window.clearTimeout(timer);
    // `micTrack` mit dabei: bleibt auch die neu geoeffnete Spur stumm, aendert
    // sich `mic` nicht -- ohne neue Spur als Anlass kaeme kein zweiter Versuch.
  }, [mic, visible, micTrack]);

  const restartMic = () => {
    micRestarts.current = 0;
    setMicStuck(false);
    setReopen((n) => n + 1);
  };

  /*
   * Sobald freigegeben und Kamera laeuft: der Regie melden, dass Bild da ist --
   * und nach jeder neuen Verbindung noch einmal. Reisst die WebSocket kurz ab
   * (WLAN-Wackler), fuehrt der Server die Quelle als „verbindet neu" und
   * beendet sie nach der Schonfrist, wenn sich das Bild nicht zurueckmeldet.
   * Das Merkzeichen gilt deshalb pro Verbindung, nicht pro Quelle: mit der
   * Quelle allein blieb die Meldung nach dem Wiederverbinden aus, und 25
   * Sekunden spaeter war das Hauptbild fuer alle schwarz.
   */
  useEffect(() => {
    if (!mySource || mySource.status !== 'approved' || !stream) return;
    if (mySource.live && !mySource.reconnecting) return;
    const key = `${mySource.id}@${mySource.connectionId}`;
    if (liveSentFor.current === key) return;
    liveSentFor.current = key;
    send({ t: 'stream:live', live: true, sourceId: mySource.id });
  }, [mySource, stream, send]);

  const status = !mySource
    ? 'idle'
    : mySource.status === 'pending'
      ? 'pending'
      : mySource.status === 'rejected'
        ? 'rejected'
        : isOnAir
          ? 'onair'
          : 'approved';

  /*
   * Die Ampel: Rot heisst nicht im Stream, Gelb „mach dich bereit" von der
   * Regie, Gruen im Bild. Bei Gruen sagt der Satz dazu, was davon wirklich
   * rausgeht -- ein abgeschaltetes Mikro aendert nichts daran, dass man im
   * Bild ist, soll aber nicht verschwiegen werden.
   */
  const light = onAirLight(mySource, state.activeSourceId, state.overlaySourceId);
  const seen = camOn(feed);
  const heard = micOn(feed);
  const lightText =
    light === 'green'
      ? `ON AIR${isOnAir ? '' : ' im kleinen Fenster'} — ${
          seen && heard
            ? 'man sieht und hört dich'
            : seen
              ? 'man sieht dich, dein Mikro ist aus'
              : heard
                ? 'man hört dich, dein Bild ist aus'
                : 'Bild und Mikro sind aus'
        }`
      : light === 'yellow'
        ? 'Mach dich bereit — gleich bist du im Stream'
        : status === 'idle'
          ? stream
            ? 'Nicht im Stream — Kamera bereit, noch nicht angefragt'
            : 'Nicht im Stream — Kamera wird geöffnet'
          : status === 'pending'
            ? 'Nicht im Stream — wartet auf Freigabe'
            : status === 'rejected'
              ? 'Nicht im Stream — Anfrage abgelehnt'
              : 'Freigegeben — noch nicht im Stream';

  /*
   * Gelb und Gruen kommen von der Regie, waehrend man womoeglich gar nicht
   * aufs Display schaut -- also kurz vibrieren, wo das Telefon es kann
   * (Android; Safari am iPhone kennt `vibrate` nicht). Nicht beim Laden der
   * Seite, nur beim Wechsel.
   */
  const lastLight = useRef(light);
  useEffect(() => {
    if (lastLight.current === light) return;
    lastLight.current = light;
    if (light === 'red') return;
    try {
      navigator.vibrate?.(light === 'green' ? [120, 80, 120, 80, 120] : [250]);
    } catch {
      /* Ohne Vibration bleibt die Ampel auf dem Display. */
    }
  }, [light]);

  return (
    <div className="broadcast">
      <div className="stage-bar">
        <Link to={`/events/${slug}/live`} className="btn btn--sm btn--onstage">
          ← <span className="only-wide">Hauptstream</span>
        </Link>
        <div>
          <div className="stage-bar__title">{state.event?.title ?? 'Senden'}</div>
          <div className="stage-bar__sub">
            {state.self ? `${state.self.displayName} · ${roleLabel[state.self.role]}` : '…'}
            {state.self?.bib ? ` · Nr. ${state.self.bib}` : ''}
          </div>
        </div>
        <div className="stage-bar__spacer" />
        <ConnPill status={state.status} />
        <AppearanceControls />
      </div>

      <div className="broadcast__body">
        <div className="broadcast__card">
          <OnAirLamp light={light} text={lightText} size="lg" className="broadcast__status" />

          {/* Bewusst nur eine mittelgrosse Box: der Rest des Bildschirms
              gehoert den Bedienelementen. Beim Senden schaut man auf die
              Knoepfe, nicht auf sich selbst. */}
          <div className="broadcast__view" data-light={light}>
            <video ref={videoRef} autoPlay playsInline muted />
            {!stream && (
              <div className="broadcast__overlay">
                <b>Kamera wird geöffnet …</b>
                {camError && <span>{camError}</span>}
              </div>
            )}
            {stream && !own.cam ? (
              <div className="broadcast__overlay">
                <b>Kamera aus</b>
                <span>Im Stream läuft das Standardbild, bis du sie wieder einschaltest.</span>
              </div>
            ) : (
              stream &&
              mySource &&
              !mySource.feed.cam && (
                <div className="broadcast__overlay">
                  <b>Bild abgeblendet</b>
                  <span>Die Regie zeigt gerade das Standardbild.</span>
                </div>
              )
            )}
            {/* Ob das Mikrofon wirklich Ton liefert -- Bild ohne Ton sieht
                man sonst erst im Stream. */}
            <LevelMeter stream={stream} off={!micOn(feed)} className="level--view" />
            {stream && (
              <button
                type="button"
                className="broadcast__flip"
                onClick={() => setFacing((f) => (f === 'user' ? 'environment' : 'user'))}
                title="Vordere oder hintere Kamera"
                aria-label="Kamera wechseln"
              >
                ⇄
              </button>
            )}
          </div>

          <div className="broadcast__panel">
        {/* Die eigenen Schalter stehen ganz oben: wenn hinter einem gerade
            etwas passiert, das nicht in den Stream soll, wird nicht gesucht. */}
        {stream && (
          <div className="feed feed--self">
            <button
              type="button"
              className={`feed__btn${own.mic ? '' : ' is-off'}`}
              aria-pressed={!own.mic}
              title={
                own.mic
                  ? 'Mikro ausschalten — im Stream hört man dich dann nicht.'
                  : 'Mikro wieder einschalten'
              }
              onClick={() => setOwn((o) => ({ ...o, mic: !o.mic }))}
            >
              <span aria-hidden>🎤</span>
              {own.mic ? 'Mikro an' : 'Mikro aus'}
            </button>
            <button
              type="button"
              className={`feed__btn${own.cam ? '' : ' is-off'}`}
              aria-pressed={!own.cam}
              title={
                own.cam
                  ? 'Kamera ausschalten — im Stream läuft dann das Standardbild.'
                  : 'Kamera wieder einschalten'
              }
              onClick={() => setOwn((o) => ({ ...o, cam: !o.cam }))}
            >
              <span aria-hidden>🎥</span>
              {own.cam ? 'Kamera an' : 'Kamera aus'}
            </button>
            {/* Gleich neben Mikro und Kamera: weiter unten im Kasten fand ihn am Handy niemand. */}
            {!clipsOff && (
              <button
                type="button"
                className="feed__btn feed__btn--clip"
                title="Video- oder Sprachnachricht an die Regie"
                onClick={() => setClipOpen(true)}
              >
                <span aria-hidden>🎬</span>
                Botschaft
              </button>
            )}
          </div>
        )}
        {status === 'pending' && !clipsOff && (
          <div className="clip-hint">
            <span>
              <b>Die Freigabe dauert?</b> Schick der Regie eine Video- oder Sprachnachricht — sie kann sie später im
              Stream zeigen.
            </span>
            <button type="button" className="btn btn--sm" onClick={() => setClipOpen(true)}>
              🎬 Aufnehmen
            </button>
          </div>
        )}
        {camError && <div className="notice notice--stage">{camError}</div>}
        {state.error && <div className="notice notice--stage">{state.error.message}</div>}
        {feedNote && <div className="notice notice--stage">{feedNote}</div>}
        {stream && mic === 'none' && (
          <div className="notice notice--stage">
            Kein Mikrofon verfügbar (keins gefunden, belegt oder nicht erlaubt) — es geht nur dein Bild raus.
          </div>
        )}
        {talk.length > 0 && (
          <div className="talk-note">
            <span>
              🎧{' '}
              {!micOn(feed)
                ? 'Die Regie spricht mit dir — dein Mikro ist aus, sie hört dich gerade nicht.'
                : inPicture
                  ? 'Du bist live — die Regie hört dich und spricht mit dir.'
                  : 'Die Regie spricht mit dir — das geht nicht in den Stream.'}
            </span>
            <LevelMeter stream={talk[0]} />
            {talkAudio.blocked && (
              <button className="btn btn--sm" onClick={talkAudio.resume}>
                🔈 Regie hören
              </button>
            )}
          </div>
        )}
        {micStuck && (
          <div className="notice notice--stage notice--resume">
            <span>
              Dein Mikrofon ist unterbrochen (z. B. durch einen Anruf oder den Sperrbildschirm) — im Stream hört man
              dich gerade nicht.
            </span>
            <button className="btn btn--sm" onClick={restartMic}>
              Mikro neu starten
            </button>
          </div>
        )}

        <div className="broadcast__meta">
          <span>
            Zuschauer an deiner Kamera: <b>{subscriberCount}</b>
          </span>
          <span style={{ marginLeft: 'auto' }}>
            {state.activeSourceId ? 'Hauptbild belegt' : 'Hauptbild frei'}
          </span>
        </div>

        {!mySource || mySource.status === 'rejected' ? (
          <>
            <div className="stack gap-8">
              <label className="stage-bar__sub">Wo stehst du? Das sieht die Regie.</label>
              <input
                className="label-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={60}
                placeholder="z. B. Km 5, Nordkurve"
              />
              <div className="chip-row">
                {labelPresets.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={`chip${label === preset ? ' is-active' : ''}`}
                    onClick={() => setLabel(preset)}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>
            {mySource?.status === 'rejected' && (
              <div className="notice notice--stage">
                Die Regie hat abgelehnt. Du kannst es mit einem anderen Standort erneut versuchen.
              </div>
            )}
            {fence && (
              <p className="stage-bar__sub">
                Dieses Event hat einen Sendebereich ({fence.mode === 'point' ? 'Umkreis' : 'Strecke'},{' '}
                {fence.radiusMeters >= 1000 ? `${(fence.radiusMeters / 1000).toFixed(1)} km` : `${fence.radiusMeters} m`}
                ).{' '}
                {fence.enforcement === 'block'
                  ? 'Ohne Standort vor Ort geht es nicht.'
                  : 'Dein Standort wird der Regie angezeigt.'}
              </p>
            )}
            {geoError && <div className="notice notice--stage">{geoError}</div>}
            {requestStuck && (
              <div className="notice notice--stage">
                Die Anfrage ist nicht bei der Regie angekommen — die Verbindung zum Server hakt gerade. Bitte noch
                einmal versuchen; hilft das nicht, die Seite neu laden.
              </div>
            )}
            <button
              className="btn btn--lg btn--block"
              disabled={!stream || state.status !== 'open' || locating || requesting}
              onClick={() => void requestStream()}
            >
              {locating
                ? 'Standort wird bestimmt …'
                : requesting
                  ? 'Anfrage wird gesendet …'
                  : 'Kamera zur Freigabe anbieten'}
            </button>
            {/* Ein grauer Knopf ohne Grund sieht aus wie ein kaputter. */}
            {!locating && !requesting && (!stream || state.status !== 'open') && (
              <p className="broadcast__why">
                {state.status !== 'open'
                  ? 'Keine Verbindung zum Server — die Seite verbindet sich neu …'
                  : camError
                    ? 'Ohne Kamera geht keine Anfrage.'
                    : 'Warte auf die Kamera …'}
              </p>
            )}
            {!stream && camError && (
              <button type="button" className="btn btn--block btn--ghost" onClick={() => setReopen((n) => n + 1)}>
                Kamera erneut öffnen
              </button>
            )}
          </>
        ) : (
          <>
            <div className="broadcast__meta">
              <span>
                Angemeldet als <b>{mySource.label}</b>
              </span>
              {fence && (
                <span
                  className={`geotag geotag--${mySource.geo.status}`}
                  style={{ marginLeft: 'auto' }}
                  title="Standort beim Anfragen"
                >
                  {geoStatusLabel[mySource.geo.status]}
                  {mySource.geo.distanceMeters !== null && mySource.geo.status === 'outside'
                    ? ` · ${mySource.geo.distanceMeters} m`
                    : ''}
                </span>
              )}
            </div>
            <button
              className="btn btn--block btn--ghost"
              onClick={() => {
                liveSentFor.current = null;
                send({ t: 'stream:cancel' });
              }}
            >
              Senden beenden
            </button>
            {status === 'pending' && (
              <p className="stage-bar__sub">
                Die Anfrage liegt jetzt im Regie-Panel. Lass die Seite offen — sobald freigegeben wird, geht dein Bild
                automatisch raus.
              </p>
            )}
          </>
        )}

        {/* Wer auf die Freigabe wartet oder gerade nicht ins Bild kommt, kann
            der Regie etwas aufnehmen -- mit derselben Kamera wie oben. */}
        <ClipRecorder
          slug={slug}
          connectionId={state.self?.connectionId ?? null}
          mine={state.myClips}
          send={send}
          source={stream}
          onSourceHd={setClipHd}
          open={clipOpen}
          onOpenChange={setClipOpen}
        />
          </div>
          <StageLegal />
        </div>
      </div>
    </div>
  );
}
